import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { ProductDetail } from '../product/types';
import { resolveQuantityConversion, type ActiveUnit } from './quantity-conversion';
import type { LocationStockItem, TransferResponse } from './types';

export interface TransferLine {
  readonly key: string;
  fromLocationId: string | null;
  batchId: string | null;
  batchNo: string;
  productId: string | null;
  qty: string;
  unitId: string;
  toLocationId: string | null;
}

const emptyLine = (): TransferLine => ({
  key: crypto.randomUUID(),
  fromLocationId: null,
  batchId: null,
  batchNo: '',
  productId: null,
  qty: '',
  unitId: '',
  toLocationId: null,
});

/**
 * 转移请求体构造——单条与批量转移共用同一个函数（1 行 = 单条，N 行 = 批量），
 * 抽成独立函数导出以便直接测试"共用同一形状"这条不变量（M6-M6），不需要
 * 通过完整渲染多行编辑 UI 交互流程去间接验证。
 */
export function buildTransferBody(lines: readonly TransferLine[]): Record<string, unknown> {
  return {
    lines: lines.map((l) => ({
      batchId: l.batchId,
      productId: l.productId,
      quantity: { value: l.qty.trim(), unitId: l.unitId },
      fromLocationId: l.fromLocationId,
      toLocationId: l.toLocationId,
    })),
  };
}

/**
 * 转移抽屉（单条 / 批量，Spec §21/§22、UX F4/F5）。用户批准的统一端点设计
 * 在这里的落点：不存在"单条转移组件"与"批量转移组件"两个平行实现——
 * 就是同一个多行表单，1 行是单条，N 行是批量，共用同一次
 * `POST /inventory/transfer` 提交。
 *
 * 交互方向选了"位置/批次先行"而不是"产品先行"：转移的心智模型是"把这批货
 * 从这里挪到那里"，先选来源位置能直接看到那里有什么批次可挪，比先选产品
 * 再反查它在哪个位置更贴合操作顺序。
 */
export function TransferDrawer({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseId = getCurrentWarehouseId()!;
  const [lines, setLines] = useState<TransferLine[]>([emptyLine()]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [success, setSuccess] = useState<TransferResponse | null>(null);
  const [preview, setPreview] = useState<TransferResponse | null>(null);

  const updateLine = (key: string, patch: Partial<TransferLine>): void =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const buildBody = (): Record<string, unknown> => buildTransferBody(lines);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<TransferResponse>('/inventory/transfer', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: buildBody(),
      }),
    onSuccess: (result) => {
      setSuccess(result);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // 预览：dry-run，不消耗 idempotency-key、不产生单据号（docNo 恒为 null，
  // 后端在 preview() 路径里明确不承诺——见 transfer.service.ts 顶部注释）。
  const previewMutation = useMutation({
    mutationFn: () =>
      apiFetch<TransferResponse>('/inventory/transfer/preview', {
        method: 'POST',
        body: buildBody(),
      }),
    onSuccess: (result) => setPreview(result),
  });

  const linesValid =
    lines.length > 0 &&
    lines.every(
      (l) =>
        l.productId && l.qty.trim() && l.unitId && l.fromLocationId && l.toLocationId && l.batchId,
    );
  const canSubmit = linesValid && !mutation.isPending;

  const transferAnother = (): void => {
    setSuccess(null);
    setIdempotencyKey(crypto.randomUUID());
    setLines([emptyLine()]);
    mutation.reset();
    previewMutation.reset();
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{t('transfer.title')}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>

        {success ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <p className="badge badge--success" style={{ marginBottom: 'var(--space-3)' }}>
              {t('transfer.successTitle')}
            </p>
            {success.docNo && (
              <p>
                {t('transfer.successDetail', { doc: success.docNo, count: success.lines.length })}
              </p>
            )}
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
              <button className="btn btn--primary" onClick={transferAnother}>
                {t('transfer.another')}
              </button>
              <button className="btn btn--ghost" onClick={onClose}>
                {t('app.close')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
            {lines.map((line, i) => (
              <TransferLineEditor
                key={line.key}
                line={line}
                index={i}
                warehouseId={warehouseId}
                onChange={(patch) => updateLine(line.key, patch)}
                onRemove={
                  lines.length > 1
                    ? () => setLines((ls) => ls.filter((l) => l.key !== line.key))
                    : undefined
                }
              />
            ))}

            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setLines((ls) => [...ls, emptyLine()])}
              style={{ marginTop: 'var(--space-2)' }}
            >
              {t('transfer.addLine')}
            </button>

            {preview && (
              <div
                className="card"
                style={{
                  padding: 'var(--space-3)',
                  marginTop: 'var(--space-3)',
                  borderLeft: '3px solid var(--color-info)',
                }}
              >
                <p style={{ marginBottom: 'var(--space-2)' }}>{t('transfer.previewTitle')}</p>
                {preview.lines.map((l) => (
                  <div key={l.batchId} className="row" style={{ gap: 'var(--space-2)' }}>
                    <span>{l.batchNo}</span>
                    <span className="field-hint">
                      {t('transfer.previewLine', {
                        qty: l.qtyBase,
                        from: l.fromQtyAfter,
                        to: l.toQtyAfter,
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {(() => {
              const error = mutation.error ?? previewMutation.error;
              return (
                error instanceof ApiError && (
                  <p className="field-error">
                    {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
                  </p>
                )
              );
            })()}

            <div className="row row--between" style={{ marginTop: 'var(--space-4)' }}>
              <button
                className="btn btn--ghost"
                disabled={!linesValid || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
              >
                {previewMutation.isPending ? t('app.loading') : t('transfer.preview')}
              </button>
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button className="btn btn--ghost" onClick={onClose}>
                  {t('app.cancel')}
                </button>
                <button
                  className="btn btn--primary"
                  disabled={!canSubmit}
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? t('app.loading') : t('transfer.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function TransferLineEditor({
  line,
  index,
  warehouseId,
  onChange,
  onRemove,
}: {
  readonly line: TransferLine;
  readonly index: number;
  readonly warehouseId: string;
  readonly onChange: (patch: Partial<TransferLine>) => void;
  readonly onRemove?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  const { data: fromStock } = useQuery({
    queryKey: ['inventory', 'location-stock', line.fromLocationId],
    queryFn: () =>
      apiFetch<{ items: LocationStockItem[] }>(`/inventory/locations/${line.fromLocationId}/stock`),
    enabled: line.fromLocationId !== null,
  });
  const { data: product } = useQuery({
    queryKey: ['transfer', 'product', line.productId],
    queryFn: () => apiFetch<ProductDetail>(`/products/${line.productId}`),
    enabled: line.productId !== null,
  });
  const activeUnits: ActiveUnit[] = useMemo(
    () => (product?.units ?? []).filter((u) => !u.archivedAt),
    [product],
  );
  const selectedUnit = activeUnits.find((u) => u.uomId === line.unitId);
  const conversion = useMemo(
    () => resolveQuantityConversion(line.qty, selectedUnit, activeUnits),
    [line.qty, selectedUnit, activeUnits],
  );

  const pickBatch = (item: LocationStockItem): void =>
    onChange({
      batchId: item.batchId,
      batchNo: item.batchNo,
      productId: item.productId,
      unitId: '',
    });

  return (
    <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      <div className="row row--between">
        <strong>{t('transfer.lineLabel', { index: index + 1 })}</strong>
        {onRemove && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onRemove}>
            {t('app.cancel')}
          </button>
        )}
      </div>

      <label className="field">
        <span>{t('transfer.fromLocation')}</span>
        <LocationTreeSelect
          warehouseId={warehouseId}
          value={line.fromLocationId}
          onChange={(id) =>
            onChange({
              fromLocationId: id,
              batchId: null,
              batchNo: '',
              productId: null,
              unitId: '',
            })
          }
          allowRoot={false}
        />
      </label>

      {line.fromLocationId && !line.batchId && (
        <div className="field">
          <span>{t('outbound.batch')}</span>
          {(fromStock?.items.length ?? 0) === 0 ? (
            <p className="field-hint">{t('outbound.noBatchesHere')}</p>
          ) : (
            (fromStock?.items ?? []).map((item) => (
              <button
                key={item.batchId}
                type="button"
                className="tree__row"
                onClick={() => pickBatch(item)}
              >
                <span style={{ flex: 2 }}>{item.productName}</span>
                <span style={{ width: '120px' }}>{item.batchNo}</span>
                <span className="field-hint" style={{ width: '80px', textAlign: 'right' }}>
                  {item.onHandBase}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {line.batchId && product && (
        <>
          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
            <span>
              <strong>{product.name}</strong> · {line.batchNo}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onChange({ batchId: null, batchNo: '', productId: null, unitId: '' })}
            >
              {t('inbound.changeProduct')}
            </button>
          </div>

          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: '140px' }}>
              <span>{t('inbound.stepQuantity')}</span>
              <input
                inputMode="decimal"
                value={line.qty}
                onChange={(e) => onChange({ qty: e.target.value })}
                aria-invalid={conversion?.error != null}
              />
            </label>
            <label className="field" style={{ maxWidth: '160px' }}>
              <span>{t('units.unit')}</span>
              <select value={line.unitId} onChange={(e) => onChange({ unitId: e.target.value })}>
                <option value="">{t('inbound.pickUnit')}</option>
                {activeUnits.map((u) => (
                  <option key={u.uomId} value={u.uomId}>
                    {u.uomCode}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {conversion?.error && (
            <span className="field-error">
              {t(`errors:${conversion.error}`, {
                defaultValue: conversion.error,
                ...conversion.errorParams,
              })}
            </span>
          )}

          <label className="field">
            <span>{t('transfer.toLocation')}</span>
            <LocationTreeSelect
              warehouseId={warehouseId}
              value={line.toLocationId}
              onChange={(id) => onChange({ toLocationId: id })}
              allowRoot={false}
            />
          </label>
        </>
      )}
    </div>
  );
}
